import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-hot-toast';
import { PrimaryButton, SecondaryButton } from '../components/buttons';
import { useWizard } from '../context/WizardCtx';
import { saveDraft } from '../utils/draftStorage';
import { VendedorQuota, ExcelFilters, DataProductionRecord } from '../types/wizard';
import { ArrowRight, Save, Upload, Plus, Trash2, FileSpreadsheet, AlertCircle, Settings, Factory, Calendar, BarChart3 } from 'lucide-react';
import { useEstruturaInterna } from '../hooks/useEstruturaInterna';
import { VendedorDropdown } from '../components/EstruturaDropdowns';
import { extractFactoriesFromExcel, processExcelFile } from '../utils/excelProcessor';
import { calculateDailyQuotas } from '../utils/quotaCalculator';

/**
 * QuotaTableStep props
 */
interface QuotaTableStepProps {
    onNext?: () => void;
}

/**
 * Vendor row with unique ID for React keys
 */
interface VendorRow extends VendedorQuota {
    id: string;
}

/**
 * Input state for percentage editing
 */
interface PercentageInputState {
    [vendorId: string]: string;
}

/**
 * Step 1: Production Upload + Vendor Quotas - Upload XLS file and set monthly percentage quotas
 */
export const QuotaTableStep: React.FC<QuotaTableStepProps> = ({ onNext }) => {
    const { data, updateVendedorQuotas, updateDailyQuotas, updateProductionFile, setData } = useWizard();
    const [vendors, setVendors] = useState<VendorRow[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [vendorIdCounter, setVendorIdCounter] = useState(1);
    const [validationErrors, setValidationErrors] = useState<string[]>([]);
    const [percentageInputs, setPercentageInputs] = useState<PercentageInputState>({});

    // Ref to prevent useEffect loop when updating vendors from internal state changes
    const isInternalUpdate = useRef(false);

    // Production data processing states
    const [showProductionProcessor, setShowProductionProcessor] = useState(false);
    const [availableFactories, setAvailableFactories] = useState<string[]>([]);
    const [isLoadingFactories, setIsLoadingFactories] = useState(false);
    const [filters, setFilters] = useState<ExcelFilters>({
        factory: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear()
    });
    const [isProcessing, setIsProcessing] = useState(false);
    const [processedProductionData, setProcessedProductionData] = useState<DataProductionRecord[] | null>(null);
    const [isProcessingVolume, setIsProcessingVolume] = useState(false);

    // Load estrutura interna data
    const { data: estruturaData, isLoading: isLoadingEstrutura, error: estruturaError } = useEstruturaInterna();

    /**
     * Initialize vendor data from existing wizard data
     * Only runs when not triggered by internal updates to prevent loops
     */
    useEffect(() => {
        // Skip if this update was triggered internally to prevent loops
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            return;
        }

        if (data.cota_vendedores.length > 0) {
            const vendorsWithId = data.cota_vendedores.map((vendor, index) => ({
                ...vendor,
                id: `vendor-${index + 1}-${Date.now()}-${index}`,
            }));
            setVendors(vendorsWithId);
            setVendorIdCounter(vendorsWithId.length + 1);
        } else {
            // Create initial vendors with equal distribution
            const initialVendors: VendorRow[] = [
                { id: `vendor-1-${Date.now()}`, vendedor: '', fatia_percentual: 33.33 },
                { id: `vendor-2-${Date.now() + 1}`, vendedor: '', fatia_percentual: 33.33 },
                { id: `vendor-3-${Date.now() + 2}`, vendedor: '', fatia_percentual: 33.34 },
            ];
            setVendors(initialVendors);
            setVendorIdCounter(4);
        }

        // Clear any local input state when vendors change
        setPercentageInputs({});
    }, [data.cota_vendedores]);

    /**
     * Validate vendor data and production requirements
     */
    const validateVendors = useCallback((): string[] => {
        const errors: string[] = [];

        // Check if there's at least one vendor
        if (vendors.length === 0) {
            errors.push('Adicione pelo menos um vendedor');
        }

        // Check for empty vendor names
        const hasEmptyNames = vendors.some(vendor => !vendor.vendedor.trim());
        if (hasEmptyNames) {
            errors.push('Todos os vendedores devem ter nomes válidos');
        }

        // Check for duplicate names
        const uniqueNames = new Set(vendors.filter(v => v.vendedor.trim()).map(v => v.vendedor.trim()));
        if (uniqueNames.size !== vendors.filter(v => v.vendedor.trim()).length) {
            errors.push('Nomes de vendedores devem ser únicos');
        }

        // Critical: Check if total percentage equals 100%
        const total = vendors.reduce((sum, vendor) => sum + vendor.fatia_percentual, 0);
        if (Math.abs(total - 100) > 0.01) { // Allow for small floating point errors
            errors.push(`Soma das fatias deve ser 100%. Atual: ${total.toFixed(2)}%`);
        }

        // Check for negative percentages
        const hasNegative = vendors.some(vendor => vendor.fatia_percentual < 0);
        if (hasNegative) {
            errors.push('Fatias não podem ser negativas');
        }

        // MANDATORY: Check if max daily volume is available from production file
        if (isProcessingVolume) {
            errors.push('Aguarde o processamento do arquivo de produção...');
        } else if (!data.maxDailyVolume || data.maxDailyVolume <= 0) {
            errors.push('É necessário fazer upload da planilha de produção e aguardar o cálculo do limite diário');
        }

        return errors;
    }, [vendors, data.maxDailyVolume, isProcessingVolume]);

    /**
     * Update validation errors whenever data changes
     */
    useEffect(() => {
        const errors = validateVendors();
        setValidationErrors(errors);
    }, [vendors, data.maxDailyVolume, isProcessingVolume]); // Use direct dependencies instead of validateVendors



    /**
     * Check if current data is valid
     */
    const isDataValid = validationErrors.length === 0;

    /**
     * Get total percentage
     */
    const getTotalPercentage = () => {
        return vendors.reduce((sum, vendor) => sum + vendor.fatia_percentual, 0);
    };

    /**
     * Add a new vendor row
     */
    const addVendor = () => {
        const uniqueId = `vendor-${vendorIdCounter}-${Date.now()}`;
        const newVendor: VendorRow = {
            id: uniqueId,
            vendedor: '',
            fatia_percentual: 0,
        };
        setVendors(prev => [...prev, newVendor]);
        setVendorIdCounter(prev => prev + 1);
    };

    /**
     * Remove vendor row
     */
    const removeVendor = (id: string) => {
        setVendors(prev => prev.filter(vendor => vendor.id !== id));
    };

    /**
     * Recalculate daily quotas when vendors change
     */
    const recalculateDailyQuotas = useCallback((updatedVendors: VendorRow[]) => {
        console.log('🔄 recalculateDailyQuotas called with vendors:', updatedVendors);

        // Only recalculate if we have production data
        if (!data.excel_production_data?.processedData?.productionData) {
            console.log('⚠️ No production data available for quota calculation');
            return;
        }

        const vendorQuotas = updatedVendors.map(v => ({
            vendedor: v.vendedor,
            fatia_percentual: v.fatia_percentual
        }));

        console.log('🔄 Vendor quotas being sent to calculateDailyQuotas:', vendorQuotas);

        const dailyQuotas = calculateDailyQuotas(
            vendorQuotas,
            data.excel_production_data.processedData.productionData,
            data.excel_production_data.filters.factory,
            data.excel_production_data.filters.year,
            data.excel_production_data.filters.month
        );

        console.log('🔄 Daily quotas calculated:', dailyQuotas.length, 'entries');
        updateDailyQuotas(dailyQuotas);
    }, [data.excel_production_data]); // Remove updateDailyQuotas from dependencies to prevent loop

    /**
     * Handle percentage input change (local state only)
     */
    const handlePercentageInputChange = (vendorId: string, value: string) => {
        setPercentageInputs(prev => ({
            ...prev,
            [vendorId]: value
        }));
    };

    /**
     * Handle percentage input blur (commit to vendor state)
     */
    const handlePercentageInputBlur = (vendorId: string, value: string) => {
        const numericValue = parseFloat(value) || 0;
        updateVendor(vendorId, 'fatia_percentual', numericValue);

        // Clean up local input state
        setPercentageInputs(prev => {
            const updated = { ...prev };
            delete updated[vendorId];
            return updated;
        });
    };

    /**
     * Get display value for percentage input
     */
    const getPercentageDisplayValue = (vendor: VendorRow): string => {
        // If there's local input state, use it
        if (percentageInputs[vendor.id] !== undefined) {
            return percentageInputs[vendor.id];
        }

        // Otherwise, use the vendor's actual value
        return vendor.fatia_percentual === 0 ? '' : vendor.fatia_percentual.toString();
    };

    /**
     * Update vendor field
     */
    const updateVendor = (id: string, field: keyof VendorRow, value: string | number) => {
        setVendors(prev => {
            const updated = prev.map(vendor =>
                vendor.id === id ? { ...vendor, [field]: value } : vendor
            );

            // Mark as internal update to prevent useEffect loop
            isInternalUpdate.current = true;

            // Sync with wizard context
            const quotasForWizard = updated.map(({ id, ...quota }) => quota);
            updateVendedorQuotas(quotasForWizard);

            // Recalculate daily quotas if percentages changed
            if (field === 'fatia_percentual') {
                recalculateDailyQuotas(updated);
            }

            return updated;
        });
    };

    /**
     * Get available vendedores that haven't been selected yet
     */
    const getAvailableVendedores = (currentVendorId?: string) => {
        if (!estruturaData?.vendedores) return [];

        const selectedVendedores = vendors
            .filter(v => v.id !== currentVendorId && v.vendedor.trim())
            .map(v => v.vendedor);

        return estruturaData.vendedores.filter(v => !selectedVendedores.includes(v));
    };

    /**
     * Distribute percentages equally
     */
    const distributeEqually = () => {
        if (vendors.length === 0) return;

        const percentagePerVendor = 100 / vendors.length;
        setVendors(prev => {
            const updated = prev.map(vendor => ({
                ...vendor,
                fatia_percentual: percentagePerVendor
            }));

            // Mark as internal update to prevent useEffect loop
            isInternalUpdate.current = true;

            // Sync with wizard context
            const quotasForWizard = updated.map(({ id, ...quota }) => quota);
            updateVendedorQuotas(quotasForWizard);

            // Recalculate daily quotas
            recalculateDailyQuotas(updated);

            return updated;
        });

        // Clear any local input state
        setPercentageInputs({});
    };

    /**
     * Include all available vendors with zero quotas, maintaining existing quotas for already selected vendors
     */
    const includeAllVendedores = () => {
        if (!estruturaData?.vendedores) return;

        // Create map of existing vendors with their current quotas
        const existingQuotas = new Map<string, number>();
        vendors.forEach(vendor => {
            if (vendor.vendedor.trim()) {
                existingQuotas.set(vendor.vendedor, vendor.fatia_percentual);
            }
        });

        // Create vendors for all available vendedores
        const allVendors: VendorRow[] = estruturaData.vendedores.map((vendedor, index) => ({
            id: `vendor-all-${index + 1}-${Date.now()}`,
            vendedor: vendedor,
            fatia_percentual: existingQuotas.get(vendedor) || 0, // Keep existing quota or set to 0
        }));

        // Count vendors with zero quotas for equal distribution
        const vendorsWithZeroQuotas = allVendors.filter(v => v.fatia_percentual === 0);

        if (vendorsWithZeroQuotas.length > 0) {
            // Calculate remaining percentage to distribute
            const usedPercentage = allVendors.reduce((sum, v) => sum + v.fatia_percentual, 0);
            const remainingPercentage = 100 - usedPercentage;
            const equalShare = remainingPercentage / vendorsWithZeroQuotas.length;

            // Distribute remaining percentage equally among vendors with zero quotas
            allVendors.forEach(vendor => {
                if (vendor.fatia_percentual === 0) {
                    vendor.fatia_percentual = equalShare;
                }
            });
        }

        setVendors(allVendors);
        setVendorIdCounter(allVendors.length + 1);

        // Mark as internal update to prevent useEffect loop
        isInternalUpdate.current = true;

        // Sync with wizard context
        const quotasForWizard = allVendors.map(({ id, ...quota }) => quota);
        updateVendedorQuotas(quotasForWizard);

        // Recalculate daily quotas
        recalculateDailyQuotas(allVendors);

        // Clear any local input state
        setPercentageInputs({});

        toast.success(`${estruturaData.vendedores.length} vendedores incluídos`);
    };

    /**
     * Load available factories from Excel file
     */
    const loadFactoriesFromFile = async (file: File) => {
        try {
            setIsLoadingFactories(true);
            const factories = await extractFactoriesFromExcel(file);
            setAvailableFactories(factories);

            if (factories.length > 0) {
                setFilters(prev => ({ ...prev, factory: factories[0] }));
            }
        } catch (error) {
            console.error('Error extracting factories:', error);
            toast.error('Erro ao extrair fábricas do Excel');
        } finally {
            setIsLoadingFactories(false);
        }
    };

    /**
     * Process production data from Excel
     */
    const handleProcessProduction = async () => {
        if (!data.producao_planejada || !filters.factory) {
            toast.error('Selecione uma fábrica');
            return;
        }

        setIsProcessing(true);
        setIsProcessingVolume(true);
        try {
            console.log('🔄 Processing production data with filters:', filters);

            const result = await processExcelFile(data.producao_planejada, filters);

            if (result.success && result.data) {
                setProcessedProductionData(result.data.productionData);
                toast.success(`${result.data.productionData.length} registros de produção processados`);
            } else {
                toast.error(result.errorDetails?.join(', ') || 'Erro ao processar dados de produção');
                setIsProcessingVolume(false); // Reset processing state on error
            }
        } catch (error) {
            console.error('Production processing error:', error);
            toast.error('Falha ao ler produção: verifique o arquivo');
            setIsProcessingVolume(false); // Reset processing state on error
        } finally {
            setIsProcessing(false);
        }
    };

    /**
     * Confirm processed production data and calculate daily quotas
     */
    const handleConfirmProductionData = () => {
        if (!processedProductionData) return;

        // Calculate max daily volume from processed data
        const maxDailyVolume = processedProductionData.reduce((max, record) => {
            return Math.max(max, record.production);
        }, 0);

        // Calculate daily quotas for all vendors
        const currentVendorQuotas = vendors.map(v => ({
            vendedor: v.vendedor,
            fatia_percentual: v.fatia_percentual
        }));

        console.log('📊 handleConfirmProductionData - Current vendors:', vendors);
        console.log('📊 handleConfirmProductionData - Vendor quotas:', currentVendorQuotas);

        const dailyQuotas = calculateDailyQuotas(
            currentVendorQuotas,
            processedProductionData,
            filters.factory, // Use selected factory as filial
            filters.year,
            filters.month
        );

        console.log('📊 handleConfirmProductionData - Daily quotas result:', dailyQuotas.length, 'entries');

        // Store production data and max daily volume in wizard context
        const excelProductionData = {
            filters: filters,
            processedData: {
                productionData: processedProductionData,
                summary: {
                    totalDays: new Set(processedProductionData.map(r => r.date)).size,
                    totalRecords: processedProductionData.length,
                    dateRange: {
                        start: processedProductionData[0]?.date || '',
                        end: processedProductionData[processedProductionData.length - 1]?.date || ''
                    },
                    factories: Array.from(new Set(processedProductionData.map(r => r.factory))),
                    maxDailyVolume: maxDailyVolume
                }
            },
            lastProcessedAt: new Date().toISOString(),
            fileName: data.producao_planejada?.name || 'unknown'
        };

        setData(prev => ({
            ...prev,
            maxDailyVolume: maxDailyVolume,
            excel_production_data: excelProductionData
        }));

        // Update daily quotas in wizard context
        updateDailyQuotas(dailyQuotas);

        setShowProductionProcessor(false);
        setProcessedProductionData(null);
        setIsProcessingVolume(false);

        const vendorsWithQuotas = currentVendorQuotas.filter(v => v.fatia_percentual > 0).length;
        const totalQuotaVolume = dailyQuotas.reduce((sum, q) => sum + q.cota, 0);
        toast.success(`Dados confirmados! ${vendorsWithQuotas} vendedores configurados. ${dailyQuotas.length} cotas diárias calculadas (total: ${totalQuotaVolume.toFixed(2)} m³).`);
    };

    /**
     * Cancel production processing
     */
    const handleCancelProductionProcessor = () => {
        setShowProductionProcessor(false);
        setProcessedProductionData(null);
        setIsProcessingVolume(false); // Reset processing state when cancelled
    };

    /**
     * Handle file drop for production file
     */
    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0];
        if (!file) return;

        // Validate file type
        const validTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-excel.sheet.macroEnabled.12',
        ];

        if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx?|xlsm)$/i)) {
            toast.error('Formato de arquivo não suportado. Use Excel (.xlsx, .xls, .xlsm)');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('Arquivo muito grande. Máximo permitido: 10MB');
            return;
        }

        updateProductionFile(file);

        // Load factories from the uploaded file
        loadFactoriesFromFile(file);

        toast.success(`Arquivo "${file.name}" carregado com sucesso`);
    }, [updateProductionFile]);

    /**
     * Configure dropzone
     */
    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragReject,
    } = useDropzone({
        onDrop,
        accept: {
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel.sheet.macroEnabled.12': ['.xlsm'],
        },
        maxFiles: 1,
        multiple: false,
    });

    /**
     * Remove uploaded file
     */
    const removeFile = () => {
        updateProductionFile(undefined);
        toast.success('Arquivo removido');
    };

    /**
     * Format file size for display
     */
    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    /**
 * Save current vendor data to wizard context
 */
    const saveVendors = () => {
        console.log('💾 saveVendors called');
        console.log('💾 Current vendors state:', vendors);
        const errors = validateVendors();
        console.log('💾 Validation errors:', errors);

        if (errors.length === 0) {
            const vendorQuotas: VendedorQuota[] = vendors.map(({ id, ...vendor }) => vendor);
            console.log('💾 Vendor quotas to save:', vendorQuotas);

            // Verify each vendor has valid data
            vendorQuotas.forEach((vendor, index) => {
                console.log(`💾 Vendor ${index + 1}: "${vendor.vendedor}" = ${vendor.fatia_percentual}%`);
            });

            // Mark as internal update to prevent useEffect loop
            isInternalUpdate.current = true;

            updateVendedorQuotas(vendorQuotas);
            console.log('💾 updateVendedorQuotas called successfully');
            return true;
        } else {
            console.log('💾 Validation failed:', errors);
        }
        return false;
    };

    /**
     * Save draft to local storage - allows saving with basic validation
     */
    const handleSaveDraft = async () => {
        console.log('💾 handleSaveDraft called');
        setIsSaving(true);
        try {
            console.log('💾 Current data:', data);
            console.log('💾 Current vendors:', vendors);

            // For draft saving, use less strict validation
            const hasValidVendors = vendors.length > 0 && vendors.some(v => v.vendedor.trim());

            if (hasValidVendors) {
                console.log('💾 Basic validation passed, saving draft...');
                const vendorQuotas: VendedorQuota[] = vendors.map(({ id, ...vendor }) => vendor);
                console.log('💾 Vendor quotas to save:', vendorQuotas);

                // Mark as internal update to prevent useEffect loop
                isInternalUpdate.current = true;

                // Update context first
                updateVendedorQuotas(vendorQuotas);
                console.log('💾 updateVendedorQuotas called successfully');

                // Save to draft storage
                const draftData = { ...data, cota_vendedores: vendorQuotas, savedStep: 2 as const };
                console.log('💾 Draft data to save:', draftData);

                await saveDraft(draftData);
                console.log('💾 Draft saved successfully');
                toast.success('Rascunho salvo com sucesso');
            } else {
                console.log('💾 Basic validation failed - no valid vendors');
                toast.error('Adicione pelo menos um vendedor com nome para salvar');
            }
        } catch (error) {
            console.error('💾 Error saving draft:', error);
            toast.error('Falha ao salvar rascunho');
        } finally {
            setIsSaving(false);
        }
    };

    /**
     * Go to next step with validation
     */
    const handleNext = () => {
        console.log('🔍 handleNext called');
        const errors = validateVendors();
        console.log('🔍 Validation errors:', errors);

        if (errors.length > 0) {
            console.log('❌ Validation failed, showing errors');
            errors.forEach(error => toast.error(error));
            return;
        }

        console.log('✅ Validation passed, calling saveVendors');
        if (saveVendors()) {
            console.log('✅ saveVendors successful');
            toast.success('Configuração salva com sucesso!');

            console.log('🔍 onNext function:', onNext);
            console.log('🔍 Calling onNext...');
            if (onNext) {
                onNext();
                console.log('✅ onNext called successfully');
            } else {
                console.error('❌ onNext is undefined!');
            }
        } else {
            console.log('❌ saveVendors failed');
        }
    };

    const totalPercentage = getTotalPercentage();
    const isExactly100 = Math.abs(totalPercentage - 100) < 0.01;

    // Show error if CSV failed to load
    if (estruturaError) {
        return (
            <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
            >
                <div className="text-center mb-6">
                    <h2 className="text-2xl font-display font-bold text-dexco-text-dark mb-2">
                        Cotas por Vendedor
                    </h2>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                        <div className="text-left">
                            <p className="text-red-800 font-medium">Erro ao carregar estrutura interna</p>
                            <p className="text-red-600 text-sm">{estruturaError}</p>
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
        >
            {/* Header */}
            <div className="text-center">
                <div className="flex justify-center mb-4">
                    <div className="w-12 h-12 bg-dexco-green-primary rounded-lg flex items-center justify-center">
                        <Upload className="w-6 h-6 text-white" />
                    </div>
                </div>
                <h2 className="font-display font-semibold text-2xl text-dexco-text-dark mb-2">
                    Produção Planejada e Cotas
                </h2>
                <p className="text-gray-600 font-body">
                    Faça upload da planilha de produção e configure as fatias percentuais dos vendedores
                </p>
                {isLoadingEstrutura && (
                    <p className="text-sm text-blue-600 mt-2">Carregando estrutura interna...</p>
                )}
            </div>

            {/* Unified Card - Upload and Vendor Quotas */}
            <div className="bg-dexco-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6 space-y-6">
                {/* Upload Section */}
                <div>
                    <div className="mb-4">
                        <h3 className="text-lg font-display font-semibold text-dexco-text-dark mb-2">
                            Upload da Produção Planejada
                        </h3>
                        <p className="text-sm text-gray-600 font-body">
                            Upload obrigatório da planilha de produção da fábrica para calcular o limite diário de produção e configurar fatias de produção personalizada para cada vendedor.
                        </p>
                    </div>

                    {!data.producao_planejada ? (
                        <div
                            {...getRootProps()}
                            className={`
                                text-center cursor-pointer transition-colors duration-200 p-6 rounded-lg
                                ${isDragActive
                                    ? 'bg-dexco-gray-bg border-2 border-dashed border-gray-300'
                                    : isDragReject
                                        ? 'border-red-400 bg-red-50'
                                        : 'hover:border-dexco-green-primary hover:bg-dexco-gray-bg border-2 border-dashed border-gray-300'
                                }
                            `}
                        >
                            <input {...getInputProps()} />

                            <div className="mx-auto w-12 h-12 mb-3">
                                <FileSpreadsheet className="w-full h-full text-dexco-green-primary" />
                            </div>

                            <h4 className="font-body font-semibold text-dexco-text-dark mb-2">
                                {isDragActive ? 'Solte o arquivo aqui' : 'Clique ou arraste arquivo Excel'}
                            </h4>

                            <p className="text-gray-600 font-body text-sm">
                                Formatos: .xlsx, .xls, .xlsm • Máximo: 10MB
                            </p>

                            <p className="text-blue-600 font-body text-xs mt-2">
                                Processamento de dados de produção disponível
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                                        <FileSpreadsheet className="w-5 h-5 text-white" />
                                    </div>
                                    <div>
                                        <div className="font-body font-semibold text-green-800">
                                            {data.producao_planejada.name}
                                        </div>
                                        <div className="text-sm text-green-700">
                                            {formatFileSize(data.producao_planejada.size)}
                                        </div>
                                    </div>
                                </div>

                                <button
                                    onClick={removeFile}
                                    className="text-red-500 hover:text-red-700 p-2 rounded-lg hover:bg-white hover:bg-opacity-50 transition-colors duration-200"
                                    aria-label="Remover arquivo"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Production Data Processor */}
                            {!showProductionProcessor && (
                                <div className="text-center">
                                    <SecondaryButton
                                        onClick={() => setShowProductionProcessor(true)}
                                        leftIcon={<Settings className="w-4 h-4" />}
                                        className="bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100"
                                    >
                                        Processar Dados de Produção
                                    </SecondaryButton>
                                    <p className="text-xs text-gray-500 mt-2">
                                        Configure fábrica e mês para extrair dados da linha MI
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Production Data Processor */}
                {showProductionProcessor && data.producao_planejada && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-dexco-white rounded-lg border border-gray-200 p-6 space-y-6"
                    >
                        {/* Header */}
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Settings className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h3 className="font-display font-semibold text-lg text-dexco-text-dark">
                                    Dados de Produção (Linha MI)
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Configure fábrica e mês para extrair dados de produção
                                </p>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="space-y-4">
                            <h4 className="font-body font-semibold text-dexco-text-dark">
                                Filtros de Processamento
                            </h4>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                {/* Factory */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Factory className="w-4 h-4 inline mr-1" />
                                        Fábrica
                                    </label>
                                    <select
                                        value={filters.factory}
                                        onChange={(e) => setFilters(prev => ({ ...prev, factory: e.target.value }))}
                                        disabled={isLoadingFactories}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        {isLoadingFactories ? (
                                            <option>Carregando...</option>
                                        ) : (
                                            <>
                                                <option value="">Selecione uma fábrica</option>
                                                {availableFactories.map(factory => (
                                                    <option key={factory} value={factory}>
                                                        {factory}
                                                    </option>
                                                ))}
                                            </>
                                        )}
                                    </select>
                                </div>

                                {/* Month */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        Mês
                                    </label>
                                    <select
                                        value={filters.month}
                                        onChange={(e) => setFilters(prev => ({ ...prev, month: parseInt(e.target.value) }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                            <option key={month} value={month}>
                                                {new Date(2024, month - 1, 1).toLocaleString('pt-BR', { month: 'long' })}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Year */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        <Calendar className="w-4 h-4 inline mr-1" />
                                        Ano
                                    </label>
                                    <select
                                        value={filters.year}
                                        onChange={(e) => setFilters(prev => ({ ...prev, year: parseInt(e.target.value) }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                                            <option key={year} value={year}>
                                                {year}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Processed Data Preview */}
                        {processedProductionData && (
                            <div className="space-y-4">
                                <h4 className="font-body font-semibold text-dexco-text-dark flex items-center gap-2">
                                    <BarChart3 className="w-5 h-5 text-green-600" />
                                    Dados Processados
                                </h4>

                                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                                        <div>
                                            <div className="text-2xl font-bold text-green-600">
                                                {processedProductionData.length}
                                            </div>
                                            <div className="text-sm text-gray-600">Registros</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-green-600">
                                                {new Set(processedProductionData.map(r => r.date)).size}
                                            </div>
                                            <div className="text-sm text-gray-600">Dias</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-green-600">
                                                {processedProductionData[0]?.factory || filters.factory}
                                            </div>
                                            <div className="text-sm text-gray-600">Fábrica</div>
                                        </div>
                                        <div>
                                            <div className="text-2xl font-bold text-green-600">
                                                {processedProductionData.reduce((sum, r) => sum + r.production, 0).toFixed(0)}
                                            </div>
                                            <div className="text-sm text-gray-600">Total Produção</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-4 border-t border-gray-200">
                            <SecondaryButton
                                onClick={handleCancelProductionProcessor}
                                disabled={isProcessing}
                            >
                                Cancelar
                            </SecondaryButton>

                            <div className="flex flex-col sm:flex-row gap-3">
                                {!processedProductionData ? (
                                    <PrimaryButton
                                        onClick={handleProcessProduction}
                                        isLoading={isProcessing}
                                        disabled={!filters.factory || isLoadingFactories}
                                        leftIcon={<BarChart3 className="w-4 h-4" />}
                                    >
                                        {isProcessing ? 'Processando...' : 'Processar Dados'}
                                    </PrimaryButton>
                                ) : (
                                    <PrimaryButton
                                        onClick={handleConfirmProductionData}
                                        leftIcon={<BarChart3 className="w-4 h-4" />}
                                        className="bg-green-600 hover:bg-green-700 focus:ring-green-500"
                                    >
                                        Confirmar Dados
                                    </PrimaryButton>
                                )}
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Divider */}
                <div className="border-t border-gray-200"></div>

                {/* Vendor Quotas Section */}
                <div>
                    <div className="mb-4">
                        <h3 className="text-lg font-display font-semibold text-dexco-text-dark mb-2">
                            Cotas por Vendedor
                        </h3>
                        <p className="text-sm text-gray-600 font-body">
                            Configure as fatias de produção personalizadas para cada vendedor
                        </p>
                    </div>

                    {/* Total Percentage Indicator */}
                    <div className={`mb-4 p-3 rounded-lg border-2 ${isExactly100
                        ? 'border-green-200 bg-green-50 text-green-800'
                        : 'border-red-200 bg-red-50 text-red-800'
                        }`}>
                        <div className="flex justify-between items-center">
                            <span className="font-body font-semibold">Total das Fatias:</span>
                            <span className={`font-display font-bold text-lg ${isExactly100 ? 'text-green-600' : 'text-red-600'
                                }`}>
                                {totalPercentage.toFixed(2)}%
                            </span>
                        </div>
                        {!isExactly100 && (
                            <p className="text-sm mt-1">
                                A soma deve ser exatamente 100% para prosseguir
                            </p>
                        )}
                    </div>

                    {/* Mobile-optimized table */}
                    <div className="overflow-x-auto">
                        <div className="min-w-full bg-white border border-gray-200 rounded-lg">
                            {/* Table Header - Hidden on mobile, shown as cards */}
                            <div className="hidden md:grid md:grid-cols-12 gap-4 bg-dexco-gray-bg px-4 py-3 border-b border-gray-200">
                                <div className="col-span-6 text-sm font-body font-semibold text-dexco-text-dark">Vendedor</div>
                                <div className="col-span-4 text-sm font-body font-semibold text-dexco-text-dark">Fatia (%)</div>
                                <div className="col-span-2 text-sm font-body font-semibold text-dexco-text-dark">Ações</div>
                            </div>

                            {/* Table Body */}
                            <div className="divide-y divide-gray-200">
                                {vendors.map((vendor, index) => (
                                    <motion.div
                                        key={vendor.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: index * 0.05 }}
                                    >
                                        {/* Desktop Layout */}
                                        <div className="hidden md:grid md:grid-cols-12 gap-4 px-4 py-3 hover:bg-gray-50 items-center">
                                            <div className="col-span-6">
                                                <VendedorDropdown
                                                    value={vendor.vendedor}
                                                    onChange={(v) => updateVendor(vendor.id, 'vendedor', v)}
                                                    vendedores={getAvailableVendedores(vendor.id)}
                                                    disabled={isLoadingEstrutura}
                                                />
                                            </div>
                                            <div className="col-span-4">
                                                <input
                                                    type="number"
                                                    value={getPercentageDisplayValue(vendor)}
                                                    onChange={(e) => handlePercentageInputChange(vendor.id, e.target.value)}
                                                    onBlur={(e) => handlePercentageInputBlur(vendor.id, e.target.value)}
                                                    min="0"
                                                    max="100"
                                                    step="0.01"
                                                    placeholder="0.00"
                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                />
                                            </div>
                                            <div className="col-span-2 flex justify-center">
                                                {vendors.length > 1 && (
                                                    <button
                                                        onClick={() => removeVendor(vendor.id)}
                                                        className="text-red-500 hover:text-red-700 p-1 rounded transition-colors duration-200"
                                                        aria-label="Remover vendedor"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {/* Mobile Layout */}
                                        <div className="md:hidden p-4 space-y-3">
                                            <div className="flex justify-between items-start">
                                                <div className="text-sm font-semibold text-gray-700">
                                                    Vendedor {index + 1}
                                                </div>
                                                {vendors.length > 1 && (
                                                    <button
                                                        onClick={() => removeVendor(vendor.id)}
                                                        className="text-red-500 hover:text-red-700 p-1 rounded transition-colors duration-200"
                                                        aria-label="Remover vendedor"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="space-y-3">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Vendedor</label>
                                                    <VendedorDropdown
                                                        value={vendor.vendedor}
                                                        onChange={(v) => updateVendor(vendor.id, 'vendedor', v)}
                                                        vendedores={getAvailableVendedores(vendor.id)}
                                                        disabled={isLoadingEstrutura}
                                                    />
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Fatia (%)</label>
                                                    <input
                                                        type="number"
                                                        value={getPercentageDisplayValue(vendor)}
                                                        onChange={(e) => handlePercentageInputChange(vendor.id, e.target.value)}
                                                        onBlur={(e) => handlePercentageInputBlur(vendor.id, e.target.value)}
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        placeholder="0.00"
                                                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-dexco-green-primary focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Action buttons - responsive layout */}
                    <div className="space-y-3 mt-6">
                        {/* Mobile: Distribuir buttons full width */}
                        <div className="sm:hidden space-y-2">
                            <SecondaryButton
                                onClick={distributeEqually}
                                className="w-full text-sm"
                            >
                                Distribuir Igualmente
                            </SecondaryButton>
                            <SecondaryButton
                                onClick={includeAllVendedores}
                                disabled={!estruturaData?.vendedores || isLoadingEstrutura}
                                className="w-full text-sm"
                                leftIcon={
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                }
                            >
                                Incluir Todos Vendedores
                            </SecondaryButton>
                        </div>

                        {/* Desktop and Mobile: Add button and desktop distribute button */}
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                            {/* Left side: Distribute buttons (desktop only) */}
                            <div className="hidden sm:flex gap-2">
                                <SecondaryButton onClick={distributeEqually} className="text-sm">
                                    Distribuir Igualmente
                                </SecondaryButton>
                                <SecondaryButton
                                    onClick={includeAllVendedores}
                                    disabled={!estruturaData?.vendedores || isLoadingEstrutura}
                                    className="text-sm"
                                    leftIcon={
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    }
                                >
                                    Incluir Todos
                                </SecondaryButton>
                            </div>

                            {/* Right side: Add button */}
                            <div className="w-full sm:w-auto">
                                <SecondaryButton
                                    onClick={addVendor}
                                    leftIcon={<Plus className="w-4 h-4" />}
                                    className="w-full sm:w-auto text-sm"
                                >
                                    Adicionar
                                </SecondaryButton>
                            </div>
                        </div>
                    </div>

                    {/* Validation Errors */}
                    {validationErrors.length > 0 && (
                        <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <h4 className="font-body font-semibold text-red-800 mb-2">Erros de Validação:</h4>
                            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
                                {validationErrors.map((error, index) => (
                                    <li key={index}>{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6">
                <SecondaryButton
                    onClick={handleSaveDraft}
                    isLoading={isSaving}
                    leftIcon={<Save className="w-4 h-4" />}
                    aria-label="Salvar configurações atuais como rascunho"
                >
                    Salvar Rascunho
                </SecondaryButton>

                <PrimaryButton
                    onClick={handleNext}
                    disabled={!isDataValid}
                    rightIcon={<ArrowRight className="w-4 h-4" />}
                    className={!isDataValid ? 'opacity-50 cursor-not-allowed' : ''}
                >
                    Próximo: Gerar Priorização
                </PrimaryButton>
            </div>
        </motion.div>
    );
}; 